/**
 * useAdminFormatters — the one hook every admin component reaches
 * for when it needs to render a money/date/number/duration string.
 *
 * It pulls the three formatter inputs (currency, locale, timezone)
 * off the `useActiveTenant` context and curries the pure formatters
 * in `src/lib/tenant-formatters.ts`. Call-sites then read like
 *
 *   const fmt = useAdminFormatters();
 *   <td>{fmt.money(invoice.amount)}</td>
 *   <td>{fmt.dateTime(booking.startTime)}</td>
 *
 * — no `'EUR'`, no `'de-DE'`, no `Intl.DateTimeFormat({timeZone})`
 * anywhere in the call-site.
 *
 * BEHAVIOUR DURING LOADING
 * ────────────────────────
 * Before `useActiveTenant` resolves, the context falls back to the
 * platform defaults (EUR / de / Europe/Berlin). That mirrors what
 * the legacy hardcoded formatters were doing anyway, so first paint
 * looks identical for DACH tenants. Non-EU tenants see a brief flash
 * of EUR before the context loads — acceptable because the dashboard
 * is permission-gated and the regional fetch is on the critical path.
 */
'use client';

import { useMemo } from 'react';
import { useActiveTenant } from './useActiveTenant';
import {
  formatMoney,
  formatMoneyCents,
  formatNumber,
  formatPercent,
  formatShortDate,
  formatLongDate,
  formatTime,
  formatDateTime,
  formatDuration,
  resolveFormatContext,
  type TenantFormatContext,
  type FormatMoneyOptions,
} from '@/lib/tenant-formatters';

export interface AdminFormatters {
  /** Underlying context — exposed so callers can pass it to pure
   *  helpers (e.g. inside a `useMemo` body) without re-running the
   *  hook. */
  ctx: TenantFormatContext;
  /** Decimal currency amount → "€12,50" / "$12.50" / "£12.50". */
  money: (amount: number, options?: FormatMoneyOptions) => string;
  /** Integer cents → same as `money(amount / 100, ...)` but with a
   *  rounding fence to defend against float drift. */
  moneyCents: (cents: number, options?: FormatMoneyOptions) => string;
  /** Locale-formatted plain number — "1.234.567" / "1,234,567". */
  number: (n: number, options?: Intl.NumberFormatOptions) => string;
  /** `0.123` → "12,3 %" / "12.3%". */
  percent: (
    n: number,
    options?: { minimumFractionDigits?: number; maximumFractionDigits?: number },
  ) => string;
  /** Short date — "26.05.2026" / "5/26/2026". */
  date: (date: Date | number | string) => string;
  /** Full long date — "Mittwoch, 26. Mai 2026". */
  longDate: (date: Date | number | string) => string;
  /** Time only — "14:30" / "2:30 PM". */
  time: (date: Date | number | string) => string;
  /** Date + time combined — "26.05.2026, 14:30". */
  dateTime: (date: Date | number | string) => string;
  /** Minute-count duration — "1 Std 30 Min" / "1 h 30 min". */
  duration: (minutes: number) => string;
}

export function useAdminFormatters(): AdminFormatters {
  const tenant = useActiveTenant();
  return useMemo(() => {
    const ctx = resolveFormatContext({
      currency: tenant.currency,
      default_locale: tenant.defaultLocale,
      timezone: tenant.timezone,
    });
    return {
      ctx,
      money: (amount, options) => formatMoney(amount, ctx, options),
      moneyCents: (cents, options) => formatMoneyCents(cents, ctx, options),
      number: (n, options) => formatNumber(n, ctx, options),
      percent: (n, options) => formatPercent(n, ctx, options),
      date: (d) => formatShortDate(d, ctx),
      longDate: (d) => formatLongDate(d, ctx),
      time: (d) => formatTime(d, ctx),
      dateTime: (d) => formatDateTime(d, ctx),
      duration: (m) => formatDuration(m, ctx),
    };
  }, [tenant.currency, tenant.defaultLocale, tenant.timezone]);
}
